import React, { useState, useMemo } from 'react';
import { Typography } from '@/UIlibrary/Typography/Typography';
import { File, Download } from 'react-feather';
import logger from '@/logger';
import Modal from '@/UIlibrary/Modal/Modal';
import { useTranslations } from 'use-intl';

interface MediaCardProps {
	title: string;
	presignedUrl: string;
	translationKey: string;
	mimeType?: 'application/pdf' | 'image/jpeg' | 'image/png' | 'image/jpg';
}

const MediaCard: React.FC<MediaCardProps> = ({
	title,
	presignedUrl,
	translationKey,
	mimeType: providedMimeType
}) => {
	const [isPreviewOpen, setIsPreviewOpen] = useState(false);
	const t = useTranslations(translationKey);

	const mimeType = useMemo(() => {
		if (providedMimeType) return providedMimeType;

		// Extract file extension from URL
		const urlWithoutParams = presignedUrl.split('?')[0];
		const extension = urlWithoutParams.split('.').pop()?.toLowerCase();

		// Map extension to MIME type
		switch (extension) {
			case 'pdf':
				return 'application/pdf';
			case 'jpg':
			case 'jpeg':
				return 'image/jpeg';
			case 'png':
				return 'image/png';
			default:
				logger.error('Unknown file extension', { extension, url: presignedUrl });
				return 'application/pdf';
		}
	}, [presignedUrl, providedMimeType]);

	const isImage = mimeType.startsWith('image/');

	const handlePreview = () => {
		setIsPreviewOpen(true);
		logger.info('MediaCard preview opened', { title, mimeType });
	};

	const handleDownload = (e: React.MouseEvent) => {
		e.preventDefault();
		const url = presignedUrl;
		const link = document.createElement('a');
		link.href = url;
		link.setAttribute('download', title);
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		logger.info('MediaCard file downloaded', { title, mimeType });
	};

	const renderThumbnail = () => {
		if (isImage) {
			return <img src={presignedUrl} alt={title} className="w-full h-32 object-cover rounded-md" />;
		}
		if (mimeType === 'application/pdf') {
			return (
				<div className="relative w-full h-32 rounded-md overflow-hidden" onClick={handlePreview}>
					<iframe
						src={presignedUrl}
						className="w-full h-48 -mt-8 scale-75 origin-top pointer-events-none"
						title={`${title} thumbnail`}
					/>
					<div className="absolute inset-0 bg-gradient-to-b from-transparent to-white/30" />
				</div>
			);
		}
		return (
			<div className="w-full h-32 bg-slate-100 rounded-md flex items-center justify-center">
				<File size={48} className="text-slate-400" />
			</div>
		);
	};

	const renderPreview = () => {
		if (isImage) {
			return (
				<div className="flex flex-col gap-4 w-full">
					<div className="flex justify-between items-center">
						<Typography size="xl" weight="semibold">
							{title}
						</Typography>
					</div>
					<div className="flex justify-center">
						<img
							src={presignedUrl}
							alt={title}
							className="max-w-full max-h-[70vh] object-contain"
						/>
					</div>
					<div className="flex justify-center">
						<button
							onClick={handleDownload}
							className="flex items-center gap-2 px-3 py-2 rounded-md bg-blue-500 hover:bg-blue-600 text-white"
						>
							{t('Download')}
							<Download size={16} />
						</button>
					</div>
				</div>
			);
		}
		if (mimeType === 'application/pdf') {
			return (
				<div className="flex flex-col gap-4 w-full">
					<div className="flex justify-between items-center">
						<Typography size="xl" weight="semibold">
							{title}
						</Typography>
					</div>
					<iframe src={presignedUrl} className="w-full h-[70vh]" title={title} />
				</div>
			);
		}
		return null;
	};

	return (
		<>
			<div
				className="flex flex-col bg-white rounded-lg shadow-md p-4 md:p-6 gap-4 hover:bg-slate-100 cursor-pointer w-full sm:w-64"
				onClick={handlePreview}
			>
				<div className="flex flex-col grow gap-4">
					<Typography size="xl" weight="semibold">
						{title}
					</Typography>
					{renderThumbnail()}
				</div>

				<Typography size="xs" color="subtitle">
					{t('ClickToVisualize')}
				</Typography>
			</div>
			<Modal
				isOpen={isPreviewOpen}
				onClose={() => setIsPreviewOpen(false)}
				closeable={true}
				maxWidth={1024}
			>
				{renderPreview()}
			</Modal>
		</>
	);
};

export default MediaCard;
